import React, { useState, useEffect, useRef } from 'react';
import { Activity, TrendingUp, TrendingDown, Clock, CheckCircle, XCircle, SkipForward, Ban, BarChart2, RefreshCw, ChevronDown, Volume2, VolumeX, Power, Calculator, Camera } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';
import FinancialGamePlanner from './FinancialGamePlanner';
import ChartAnalyzer from './ChartAnalyzer';

// API URL helper
const getApiUrl = () => {
  const hostname = window.location.hostname;
  if (hostname === 'bonbot.biz' || hostname === 'www.bonbot.biz') {
    return 'https://bonbot.biz/api';
  }
  const backendUrl = process.env.REACT_APP_BACKEND_URL || window.location.origin;
  return `${backendUrl}/api`;
};

const API = getApiUrl();

// Timeframe options
const TIMEFRAMES = [
  { value: 'auto', label: 'Auto (Recommended)' },
  { value: '1', label: '1m' },
  { value: '2', label: '2m' },
  { value: '3', label: '3m' },
  { value: '5', label: '5m' },
  { value: '15', label: '15m' },
  { value: '60', label: '1h' },
  { value: '240', label: '4h' },
];

// Market types
const MARKET_TYPES = [
  { value: 'forex', label: 'FOREX' },
  { value: 'otc', label: 'OTC' },
  { value: 'crypto', label: 'CRYPTO' },
];

// FOREX pairs (live market)
const FOREX_PAIRS = [
  { symbol: 'EUR/USD', payout: 85, price: 1.08234 },
  { symbol: 'GBP/USD', payout: 82, price: 1.26543 },
  { symbol: 'USD/JPY', payout: 80, price: 149.234 },
  { symbol: 'AUD/USD', payout: 84, price: 0.65432 },
  { symbol: 'USD/CAD', payout: 83, price: 1.35678 },
  { symbol: 'EUR/GBP', payout: 81, price: 0.85543 },
  { symbol: 'EUR/JPY', payout: 82, price: 161.432 },
  { symbol: 'GBP/JPY', payout: 79, price: 188.765 },
  { symbol: 'AUD/JPY', payout: 80, price: 97.234 },
  { symbol: 'NZD/USD', payout: 78, price: 0.60123 },
  { symbol: 'USD/CHF', payout: 81, price: 0.88765 },
  { symbol: 'EUR/CHF', payout: 79, price: 0.96123 },
  { symbol: 'GBP/CHF', payout: 77, price: 1.12345 },
];

// OTC pairs (24/7 market)
const OTC_PAIRS = [
  { symbol: 'EUR/USD OTC', payout: 92, price: 1.08234 },
  { symbol: 'GBP/USD OTC', payout: 88, price: 1.26543 },
  { symbol: 'USD/JPY OTC', payout: 85, price: 149.234 },
  { symbol: 'AUD/USD OTC', payout: 90, price: 0.65432 },
  { symbol: 'USD/CAD OTC', payout: 92, price: 1.35678 },
  { symbol: 'EUR/GBP OTC', payout: 87, price: 0.85543 },
  { symbol: 'EUR/JPY OTC', payout: 92, price: 161.432 },
  { symbol: 'GBP/JPY OTC', payout: 85, price: 188.765 },
  { symbol: 'AUD/JPY OTC', payout: 88, price: 97.234 },
  { symbol: 'NZD/USD OTC', payout: 82, price: 0.60123 },
  { symbol: 'USD/CHF OTC', payout: 92, price: 0.88765 },
  { symbol: 'CAD/JPY OTC', payout: 92, price: 109.876 },
  { symbol: 'AUD/CAD OTC', payout: 92, price: 0.89234 },
  { symbol: 'EUR/NZD OTC', payout: 92, price: 1.80123 },
  { symbol: 'USD/BRL OTC', payout: 92, price: 4.98765 },
  { symbol: 'GOLD OTC', payout: 80, price: 2034.56 },
];

// CRYPTO pairs
const CRYPTO_PAIRS = [
  { symbol: 'BTC/USD', payout: 75, price: 43250.00 },
  { symbol: 'ETH/USD', payout: 78, price: 2650.00 },
  { symbol: 'LTC/USD', payout: 72, price: 72.50 },
  { symbol: 'XRP/USD', payout: 70, price: 0.52 },
  { symbol: 'BNB/USD', payout: 74, price: 312.00 },
  { symbol: 'SOL/USD', payout: 73, price: 98.50 },
  { symbol: 'DOGE/USD', payout: 68, price: 0.082 },
  { symbol: 'ADA/USD', payout: 71, price: 0.48 },
];

const ProScalperBot = ({ user, onSwitchToStandard }) => {
  // View state - bot, planner, or chart analyzer
  const [showPlanner, setShowPlanner] = useState(false);
  const [showChartAnalyzer, setShowChartAnalyzer] = useState(false);
  
  // Scanner state
  const [scannerEnabled, setScannerEnabled] = useState(false);
  const [isScanning, setIsScanning] = useState(false);
  
  // Settings state
  const [selectedTimeframe, setSelectedTimeframe] = useState('auto');
  const [selectedMarket, setSelectedMarket] = useState('otc');
  const [highPayoutOnly, setHighPayoutOnly] = useState(true);
  const [soundEnabled, setSoundEnabled] = useState(true);
  
  // Dropdown states
  const [showTimeframeDropdown, setShowTimeframeDropdown] = useState(false);
  const [showMarketDropdown, setShowMarketDropdown] = useState(false);
  
  // Trading pairs state
  const [tradingPairs, setTradingPairs] = useState([]);
  const [selectedPair, setSelectedPair] = useState(null);
  
  // Active signal state
  const [activeSignal, setActiveSignal] = useState(null);
  const [entryCountdown, setEntryCountdown] = useState(0);
  
  // Technical indicators
  const [indicators, setIndicators] = useState({
    fastEMA: 0,
    slowEMA: 0,
    rsi: 50,
    bbUpper: 0,
    bbBasis: 0,
    bbLower: 0,
  });
  
  // Signal history
  const [signalHistory, setSignalHistory] = useState([]);
  
  // Stats
  const [stats, setStats] = useState({ wins: 0, losses: 0, winRate: 0 });
  
  // Blocked pairs (low payout)
  const [blockedPairs, setBlockedPairs] = useState([]);
  
  // Refs
  const scanIntervalRef = useRef(null);
  const countdownRef = useRef(null);
  const priceUpdateRef = useRef(null);
  
  // Load pairs based on market type
  useEffect(() => {
    loadPairs();
    loadStats();
    loadBlockedPairs();
  }, [selectedMarket]);
  
  // Start/stop scanner
  useEffect(() => {
    if (scannerEnabled && !activeSignal) {
      startScanning();
    } else {
      stopScanning();
    }
    return () => stopScanning();
  }, [scannerEnabled, activeSignal, selectedMarket, highPayoutOnly]);
  
  // Price updates for pairs
  useEffect(() => {
    if (tradingPairs.length > 0) {
      priceUpdateRef.current = setInterval(() => {
        setTradingPairs(prev => prev.map(pair => ({
          ...pair,
          price: simulatePriceMovement(pair.price, pair.symbol),
          trend: Math.random() > 0.5 ? 'up' : 'down'
        })));
      }, 2000);
    }
    return () => {
      if (priceUpdateRef.current) clearInterval(priceUpdateRef.current);
    };
  }, [tradingPairs.length]);
  
  // Entry countdown
  useEffect(() => {
    if (activeSignal && entryCountdown > 0) {
      countdownRef.current = setInterval(() => {
        setEntryCountdown(prev => {
          if (prev <= 1) {
            clearInterval(countdownRef.current);
            if (soundEnabled) playSound('entry');
            toast.success('⏰ ENTER NOW!', { duration: 5000 });
            return 0;
          }
          return prev - 1;
        });
      }, 1000);
    }
    return () => {
      if (countdownRef.current) clearInterval(countdownRef.current);
    };
  }, [activeSignal, soundEnabled]);
  
  const loadPairs = () => {
    let pairs;
    switch (selectedMarket) {
      case 'forex':
        pairs = FOREX_PAIRS;
        break;
      case 'otc':
        pairs = OTC_PAIRS;
        break;
      case 'crypto':
        pairs = CRYPTO_PAIRS;
        break;
      default:
        pairs = OTC_PAIRS;
    }
    
    // Add trend data
    const pairsWithTrend = pairs.map(pair => ({
      ...pair,
      trend: Math.random() > 0.5 ? 'up' : 'down'
    }));
    
    setTradingPairs(pairsWithTrend);
    if (pairsWithTrend.length > 0 && !selectedPair) {
      setSelectedPair(pairsWithTrend[0]);
    }
  };
  
  const loadStats = async () => {
    // Load local stats first
    const saved = localStorage.getItem('proScalperStats');
    if (saved) {
      setStats(JSON.parse(saved));
    }
    
    // Also load server stats for PRO mode
    try {
      const token = localStorage.getItem('guestToken');
      if (token) {
        const response = await fetch(`${API}/user/profitability?token=${token}`);
        const data = await response.json();
        if (data.success && data.stats?.pro) {
          // Update stats with server PRO data
          const proStats = data.stats.pro;
          const serverStats = {
            wins: proStats.wins || 0,
            losses: proStats.losses || 0,
            winRate: proStats.win_rate || 0
          };
          // Use server stats if they have trades, otherwise keep local
          if (proStats.total_trades > 0) {
            setStats(serverStats);
            localStorage.setItem('proScalperStats', JSON.stringify(serverStats));
          }
        }
      }
    } catch (err) {
      console.error('Failed to load server stats:', err);
    }
  };
  
  const loadBlockedPairs = () => {
    const saved = localStorage.getItem('proBlockedPairs');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.expiresAt && Date.now() < data.expiresAt) {
          setBlockedPairs(data.pairs || []);
        } else {
          localStorage.removeItem('proBlockedPairs');
        }
      } catch (e) {
        console.error('Error loading blocked pairs:', e);
      }
    }
  };
  
  const saveStats = (newStats) => {
    localStorage.setItem('proScalperStats', JSON.stringify(newStats));
    setStats(newStats);
  };
  
  const simulatePriceMovement = (currentPrice, symbol) => {
    // Small random price movement
    const volatility = symbol.includes('JPY') ? 0.01 : 0.00005;
    const change = (Math.random() - 0.5) * 2 * volatility * currentPrice;
    return currentPrice + change;
  };
  
  const playSound = (type) => {
    try {
      const audioContext = new (window.AudioContext || window.webkitAudioContext)();
      const oscillator = audioContext.createOscillator();
      const gainNode = audioContext.createGain();
      
      oscillator.connect(gainNode);
      gainNode.connect(audioContext.destination);
      
      oscillator.frequency.value = type === 'entry' ? 880 : type === 'win' ? 1200 : type === 'signal' ? 660 : 400;
      oscillator.type = 'sine';
      
      gainNode.gain.setValueAtTime(0.3, audioContext.currentTime);
      gainNode.gain.exponentialRampToValueAtTime(0.01, audioContext.currentTime + 0.3);
      
      oscillator.start(audioContext.currentTime);
      oscillator.stop(audioContext.currentTime + 0.3);
    } catch (e) {
      console.log('Audio not available');
    }
  };
  
  const startScanning = () => {
    if (scanIntervalRef.current) return;
    
    setIsScanning(true);
    
    // Initial scan immediately
    setTimeout(() => scanForSignals(), 500);
    
    // Then scan every 3 seconds
    scanIntervalRef.current = setInterval(() => {
      scanForSignals();
    }, 3000);
  };
  
  const stopScanning = () => {
    if (scanIntervalRef.current) {
      clearInterval(scanIntervalRef.current);
      scanIntervalRef.current = null;
    }
    setIsScanning(false);
  };
  
  const scanForSignals = async () => {
    if (activeSignal) return;
    
    // Get eligible pairs
    const eligiblePairs = tradingPairs
      .filter(p => !blockedPairs.includes(p.symbol))
      .filter(p => !highPayoutOnly || p.payout >= 80);
    
    if (eligiblePairs.length === 0) {
      toast.warning('No eligible pairs available. Try disabling High Payout filter.');
      return;
    }
    
    // Select a random pair to analyze
    const pair = eligiblePairs[Math.floor(Math.random() * eligiblePairs.length)];
    setSelectedPair(pair);
    
    // Generate technical indicators
    const newIndicators = generateIndicators(pair);
    setIndicators(newIndicators);
    
    // Calculate signal based on indicators
    const signalResult = analyzeSignal(newIndicators, pair);
    
    if (signalResult.hasSignal) {
      // Get expiry based on selected timeframe (not auto-calculated)
      const expiry = getSelectedExpiry();
      
      const signal = {
        id: Date.now(),
        pair: pair.symbol,
        direction: signalResult.direction,
        entryPrice: pair.price,
        strength: signalResult.strength,
        payout: pair.payout,
        recommendedExpiry: expiry,
        analysisTimeframe: selectedTimeframe,
        timestamp: new Date().toISOString(),
      };
      
      setActiveSignal(signal);
      
      // ALWAYS enter at the opening of next 1-minute candle
      // Regardless of which timeframe was used for analysis
      const now = new Date();
      const secondsUntilNextMinute = 60 - now.getSeconds();
      setEntryCountdown(secondsUntilNextMinute);
      
      if (soundEnabled) playSound('signal');
      toast.success(`🎯 ${signal.direction} Signal on ${signal.pair} (${signal.strength}%)`, { duration: 5000 });
    }
  };
  
  // Get expiry based on user's selected timeframe
  const getSelectedExpiry = () => {
    switch (selectedTimeframe) {
      case '1': return '1 min';
      case '2': return '2 min';
      case '3': return '3 min';
      case '5': return '5 min';
      case '15': return '15 min';
      case '60': return '1 hour';
      case '240': return '4 hours';
      case 'auto':
      default:
        // Auto mode - recommend based on market conditions
        return '2 min'; // Default recommendation for scalping
    }
  };
  
  const generateIndicators = (pair) => {
    const price = pair.price;
    const volatility = pair.symbol.includes('JPY') ? 0.5 : 0.001;
    
    // EMA calculations (simulated)
    const fastEMA = price * (1 + (Math.random() - 0.5) * volatility);
    const slowEMA = price * (1 + (Math.random() - 0.5) * volatility * 1.5);
    
    // RSI (0-100)
    const rsi = 30 + Math.random() * 40; // Bias towards middle range with occasional extremes
    const rsiAdjusted = Math.random() > 0.7 ? (Math.random() > 0.5 ? 25 + Math.random() * 10 : 65 + Math.random() * 10) : rsi;
    
    // Bollinger Bands
    const bbBasis = price;
    const bbWidth = price * volatility * 2;
    const bbUpper = bbBasis + bbWidth;
    const bbLower = bbBasis - bbWidth;
    
    return {
      fastEMA,
      slowEMA,
      rsi: rsiAdjusted,
      bbUpper,
      bbBasis,
      bbLower,
    };
  };
  
  const analyzeSignal = (indicators, pair) => {
    let strength = 30;
    let direction = null;
    let bullishSignals = 0;
    let bearishSignals = 0;
    
    // EMA Crossover analysis
    if (indicators.fastEMA > indicators.slowEMA) {
      bullishSignals++;
      strength += 10;
    } else {
      bearishSignals++;
      strength += 10;
    }
    
    // RSI analysis
    if (indicators.rsi < 30) {
      bullishSignals += 2; // Oversold = bullish signal
      strength += 20;
    } else if (indicators.rsi > 70) {
      bearishSignals += 2; // Overbought = bearish signal
      strength += 20;
    } else if (indicators.rsi < 40) {
      bullishSignals++;
      strength += 10;
    } else if (indicators.rsi > 60) {
      bearishSignals++;
      strength += 10;
    }
    
    // Bollinger Bands analysis
    const price = pair.price;
    if (price <= indicators.bbLower * 1.01) {
      bullishSignals += 2; // Price at lower band = bullish
      strength += 15;
    } else if (price >= indicators.bbUpper * 0.99) {
      bearishSignals += 2; // Price at upper band = bearish
      strength += 15;
    }
    
    // Determine direction and if signal is strong enough
    const totalSignals = bullishSignals + bearishSignals;
    const hasSignal = totalSignals >= 3 && Math.abs(bullishSignals - bearishSignals) >= 2;
    
    if (hasSignal) {
      direction = bullishSignals > bearishSignals ? 'CALL' : 'PUT';
      // Add randomness to occasionally generate signals
      strength = Math.min(Math.max(strength + Math.floor(Math.random() * 20), 40), 95);
    }
    
    // Random chance to generate signal anyway (for demo purposes)
    if (!hasSignal && Math.random() > 0.7) {
      direction = Math.random() > 0.5 ? 'CALL' : 'PUT';
      strength = 40 + Math.floor(Math.random() * 30);
      return { hasSignal: true, direction, strength };
    }
    
    return { hasSignal, direction, strength };
  };
  
  const getStrengthLabel = (strength) => {
    if (strength >= 70) return 'STRONG';
    if (strength >= 50) return 'MODERATE';
    return 'WEAK';
  };
  
  const getStrengthColor = (strength) => {
    if (strength >= 70) return 'text-green-400';
    if (strength >= 50) return 'text-yellow-400';
    return 'text-red-400';
  };
  
  const handleTradeResult = async (result) => {
    if (!activeSignal) return;
    
    const newStats = { ...stats };
    let apiResult = null;
    
    if (result === 'win') {
      newStats.wins += 1;
      apiResult = 'win';
      if (soundEnabled) playSound('win');
      toast.success('✅ Trade Won!');
    } else if (result === 'lost') {
      newStats.losses += 1;
      apiResult = 'loss';
      if (soundEnabled) playSound('lost');
      toast.error('❌ Trade Lost');
    } else if (result === 'lowpay') {
      const newBlocked = [...blockedPairs, activeSignal.pair];
      setBlockedPairs(newBlocked);
      localStorage.setItem('proBlockedPairs', JSON.stringify({
        pairs: newBlocked,
        expiresAt: Date.now() + 3600000,
      }));
      toast.warning(`🚫 ${activeSignal.pair} blocked for 1 hour`);
    }
    
    newStats.winRate = newStats.wins + newStats.losses > 0 
      ? Math.round((newStats.wins / (newStats.wins + newStats.losses)) * 1000) / 10
      : 0;
    
    saveStats(newStats);
    
    // Report trade to backend with mode='pro'
    if (apiResult) {
      try {
        const token = localStorage.getItem('guestToken');
        if (token) {
          await fetch(`${API}/user/report-trade?token=${token}`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              result: apiResult,
              signal_id: activeSignal.id || null,
              mode: 'pro'  // Identify this as a PRO trade
            })
          });
        }
      } catch (err) {
        console.error('Failed to report trade:', err);
      }
    }
    
    // Add to history
    if (result !== 'skip') {
      const historyEntry = {
        ...activeSignal,
        result: result,
        closedAt: new Date().toISOString(),
      };
      setSignalHistory(prev => [historyEntry, ...prev].slice(0, 20));
    }
    
    // Clear signal and resume scanning
    setActiveSignal(null);
    setEntryCountdown(0);
  };
  
  const toggleScanner = () => {
    setScannerEnabled(!scannerEnabled);
    if (!scannerEnabled) {
      toast.success('🚀 Scanner Started');
    } else {
      toast.info('⏸️ Scanner Stopped');
    }
  };
  
  const formatPrice = (price, symbol) => {
    if (symbol.includes('JPY')) return price.toFixed(3);
    if (symbol.includes('BTC') || symbol.includes('GOLD')) return price.toFixed(2);
    return price.toFixed(5);
  };
  
  return (
    <>
      {/* Financial Game Planner - Flip view */}
      {showPlanner ? (
        <FinancialGamePlanner 
          user={user} 
          onBack={() => setShowPlanner(false)} 
        />
      ) : showChartAnalyzer ? (
        <ChartAnalyzer 
          user={user} 
          onBack={() => setShowChartAnalyzer(false)} 
        />
      ) : (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200 px-4 py-3 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex items-center justify-between">
          {/* Logo */}
          <div className="flex items-center gap-3">
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-gradient-to-br from-purple-600 to-blue-600 rounded-lg flex items-center justify-center">
                <BarChart2 className="w-5 h-5 text-white" />
              </div>
              <span className="font-bold text-xl text-gray-900">Binary</span>
              <span className="font-bold text-xl text-purple-600">Scalper</span>
            </div>
            <span className="text-sm text-gray-500 hidden sm:inline">PRO</span>
          </div>
          
          {/* Controls */}
          <div className="flex items-center gap-2 sm:gap-4">
            {/* Stats */}
            <div className="flex items-center gap-2 text-sm">
              <span className="flex items-center gap-1">
                <CheckCircle className="w-4 h-4 text-green-500" />
                <span className="text-gray-700 font-medium">{stats.wins}W</span>
              </span>
              <span className="flex items-center gap-1">
                <XCircle className="w-4 h-4 text-red-500" />
                <span className="text-gray-700 font-medium">{stats.losses}L</span>
              </span>
              <span className="hidden sm:inline text-gray-600">({stats.winRate}%)</span>
            </div>
            
            {/* ON/OFF Toggle */}
            <button
              onClick={toggleScanner}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-full font-medium text-sm transition-all ${
                scannerEnabled 
                  ? 'bg-green-500 text-white' 
                  : 'bg-gray-200 text-gray-600 hover:bg-gray-300'
              }`}
            >
              <Power className="w-4 h-4" />
              {scannerEnabled ? 'ON' : 'OFF'}
            </button>
            
            {/* Market Type */}
            <div className="relative">
              <button
                onClick={() => setShowMarketDropdown(!showMarketDropdown)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-green-100 text-green-700 rounded-full text-sm font-medium hover:bg-green-200"
              >
                <span className="w-2 h-2 bg-green-500 rounded-full"></span>
                {MARKET_TYPES.find(m => m.value === selectedMarket)?.label}
                <ChevronDown className="w-3 h-3" />
              </button>
              
              {showMarketDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowMarketDropdown(false)} />
                  <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    {MARKET_TYPES.map(market => (
                      <button
                        key={market.value}
                        onClick={() => {
                          setSelectedMarket(market.value);
                          setShowMarketDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 flex items-center gap-2 ${
                          selectedMarket === market.value ? 'text-green-600 font-medium' : 'text-gray-700'
                        }`}
                      >
                        <span className={`w-2 h-2 rounded-full ${selectedMarket === market.value ? 'bg-green-500' : 'bg-gray-300'}`}></span>
                        {market.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {/* Timeframe */}
            <div className="relative hidden sm:block">
              <button
                onClick={() => setShowTimeframeDropdown(!showTimeframeDropdown)}
                className="flex items-center gap-1.5 px-3 py-1.5 bg-gray-100 text-gray-700 rounded-full text-sm font-medium hover:bg-gray-200"
              >
                <Clock className="w-3 h-3" />
                {TIMEFRAMES.find(t => t.value === selectedTimeframe)?.label}
                <ChevronDown className="w-3 h-3" />
              </button>
              
              {showTimeframeDropdown && (
                <>
                  <div className="fixed inset-0 z-40" onClick={() => setShowTimeframeDropdown(false)} />
                  <div className="absolute right-0 mt-1 w-44 bg-white border border-gray-200 rounded-lg shadow-lg z-50">
                    {TIMEFRAMES.map(tf => (
                      <button
                        key={tf.value}
                        onClick={() => {
                          setSelectedTimeframe(tf.value);
                          setShowTimeframeDropdown(false);
                        }}
                        className={`w-full px-3 py-2 text-left text-sm hover:bg-gray-50 ${
                          selectedTimeframe === tf.value ? 'text-purple-600 font-medium' : 'text-gray-700'
                        }`}
                      >
                        {tf.label}
                      </button>
                    ))}
                  </div>
                </>
              )}
            </div>
            
            {/* High Payout */}
            <button
              onClick={() => setHighPayoutOnly(!highPayoutOnly)}
              className={`hidden sm:flex items-center gap-1.5 px-3 py-1.5 rounded-full text-sm font-medium transition-all ${
                highPayoutOnly 
                  ? 'bg-yellow-100 text-yellow-700' 
                  : 'bg-gray-100 text-gray-500'
              }`}
            >
              High Payout
            </button>
            
            {/* Sound */}
            <button
              onClick={() => setSoundEnabled(!soundEnabled)}
              className="p-2 hover:bg-gray-100 rounded-full"
            >
              {soundEnabled ? <Volume2 className="w-4 h-4 text-gray-600" /> : <VolumeX className="w-4 h-4 text-gray-400" />}
            </button>
            
            {/* Scanner Status */}
            <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-full">
              <span className={`w-2 h-2 rounded-full ${isScanning ? 'bg-green-500 animate-pulse' : 'bg-gray-400'}`}></span>
              <span className="text-xs text-gray-600">
                {activeSignal ? 'Signal Active' : isScanning ? 'Scanning...' : 'Scanner Off'}
              </span>
            </div>
            
            {/* Live */}
            <div className="flex items-center gap-1.5">
              <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></span>
              <span className="text-xs text-green-600 font-medium">Live</span>
            </div>
            
            {/* Go Standard */}
            <button
              onClick={onSwitchToStandard}
              className="hidden lg:flex items-center gap-1.5 px-3 py-1.5 bg-purple-100 text-purple-700 rounded-full text-sm font-medium hover:bg-purple-200"
            >
              Go Standard
            </button>
          </div>
        </div>
      </header>
      
      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 py-6">
        <div className="flex gap-6">
          {/* Left Sidebar - Trading Pairs */}
          <div className="w-56 flex-shrink-0 hidden lg:block">
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <div className="flex items-center justify-between mb-4">
                <h3 className="font-semibold text-gray-900 text-sm">
                  {selectedMarket.toUpperCase()} PAIRS
                </h3>
                <span className="text-xs text-green-600 bg-green-100 px-2 py-0.5 rounded-full font-medium">
                  {tradingPairs.length} ASSETS
                </span>
              </div>
              
              <div className="space-y-1 max-h-[500px] overflow-y-auto">
                {tradingPairs.map(pair => (
                  <button
                    key={pair.symbol}
                    onClick={() => setSelectedPair(pair)}
                    className={`w-full p-2.5 rounded-lg text-left transition-all ${
                      selectedPair?.symbol === pair.symbol 
                        ? 'bg-blue-50 border border-blue-200' 
                        : 'hover:bg-gray-50 border border-transparent'
                    } ${blockedPairs.includes(pair.symbol) ? 'opacity-40' : ''}`}
                    disabled={blockedPairs.includes(pair.symbol)}
                  >
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-900 text-sm">{pair.symbol}</span>
                      <div className="flex items-center gap-1">
                        <span className={`text-xs px-1.5 py-0.5 rounded ${pair.payout >= 85 ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>
                          {pair.payout}%
                        </span>
                        {pair.trend === 'up' ? (
                          <TrendingUp className="w-3 h-3 text-green-500" />
                        ) : (
                          <TrendingDown className="w-3 h-3 text-red-500" />
                        )}
                      </div>
                    </div>
                    <div className="text-xs text-gray-500 mt-0.5">{formatPrice(pair.price, pair.symbol)}</div>
                  </button>
                ))}
              </div>
            </div>
          </div>
          
          {/* Main Content Area */}
          <div className="flex-1 space-y-6">
            {/* Active Signal Card */}
            <div className="bg-white rounded-xl border border-gray-200 p-6">
              <div className="flex items-center gap-2 mb-4">
                <Activity className="w-5 h-5 text-purple-600" />
                <h2 className="font-semibold text-gray-900">Active Signal</h2>
              </div>
              
              {activeSignal ? (
                <div className={`rounded-xl p-6 border-2 ${activeSignal.direction === 'CALL' ? 'bg-green-50 border-green-300' : 'bg-red-50 border-red-300'}`}>
                  <div className="flex items-start justify-between mb-6">
                    <div className="flex items-center gap-4">
                      <div className={`p-3 rounded-xl ${activeSignal.direction === 'CALL' ? 'bg-green-100' : 'bg-red-100'}`}>
                        {activeSignal.direction === 'CALL' ? (
                          <TrendingUp className="w-8 h-8 text-green-600" />
                        ) : (
                          <TrendingDown className="w-8 h-8 text-red-600" />
                        )}
                      </div>
                      <div>
                        <h3 className={`text-3xl font-bold ${activeSignal.direction === 'CALL' ? 'text-green-600' : 'text-red-600'}`}>
                          {activeSignal.direction}
                        </h3>
                        <p className="text-gray-600 font-medium">{activeSignal.pair}</p>
                      </div>
                    </div>
                    
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Signal Strength</p>
                      <p className={`text-3xl font-bold ${getStrengthColor(activeSignal.strength)}`}>
                        {activeSignal.strength}%
                      </p>
                      <p className={`text-sm font-medium ${getStrengthColor(activeSignal.strength)}`}>
                        {getStrengthLabel(activeSignal.strength)}
                      </p>
                    </div>
                  </div>
                  
                  {/* Entry Info */}
                  <div className="grid grid-cols-4 gap-3 mb-6">
                    <div className="text-center p-3 bg-white/70 rounded-lg">
                      <p className="text-xs text-gray-500">Entry Price</p>
                      <p className="text-lg font-bold text-gray-900">@ {formatPrice(activeSignal.entryPrice, activeSignal.pair)}</p>
                    </div>
                    
                    <div className={`text-center p-3 rounded-lg border-2 ${activeSignal.direction === 'CALL' ? 'bg-green-100 border-green-400' : 'bg-red-100 border-red-400'}`}>
                      <p className={`text-xs ${activeSignal.direction === 'CALL' ? 'text-green-700' : 'text-red-700'}`}>⚡ Next 1m Candle</p>
                      <p className={`text-2xl font-bold ${activeSignal.direction === 'CALL' ? 'text-green-700' : 'text-red-700'}`}>{entryCountdown}s</p>
                    </div>
                    
                    <div className="text-center p-3 bg-white/70 rounded-lg">
                      <p className="text-xs text-gray-500">⏱ Expiry</p>
                      <p className="text-lg font-bold text-purple-600">{activeSignal.recommendedExpiry}</p>
                    </div>
                    
                    <div className="text-center p-3 bg-blue-50 rounded-lg border border-blue-200">
                      <p className="text-xs text-blue-600">📊 Analysis TF</p>
                      <p className="text-lg font-bold text-blue-700">
                        {activeSignal.analysisTimeframe === 'auto' ? 'Auto' : TIMEFRAMES.find(t => t.value === activeSignal.analysisTimeframe)?.label || activeSignal.analysisTimeframe}
                      </p>
                    </div>
                  </div>
                  
                  {/* Trade Result Buttons */}
                  <div className="border-t border-gray-200 pt-4">
                    <p className="text-center text-gray-600 text-sm mb-4">Report your trade result to resume scanner</p>
                    <div className="flex gap-3 justify-center flex-wrap">
                      <Button onClick={() => handleTradeResult('win')} className="bg-green-500 hover:bg-green-600 text-white px-6 py-3 text-lg font-bold">
                        <CheckCircle className="w-5 h-5 mr-2" /> WIN
                      </Button>
                      <Button onClick={() => handleTradeResult('lost')} className="bg-red-500 hover:bg-red-600 text-white px-6 py-3 text-lg font-bold">
                        <XCircle className="w-5 h-5 mr-2" /> LOST
                      </Button>
                      <Button onClick={() => handleTradeResult('skip')} variant="outline" className="border-gray-300 text-gray-700 px-6 py-3 text-lg font-bold">
                        <SkipForward className="w-5 h-5 mr-2" /> SKIP
                      </Button>
                      <Button onClick={() => handleTradeResult('lowpay')} className="bg-yellow-500 hover:bg-yellow-600 text-white px-6 py-3 text-lg font-bold">
                        <Ban className="w-5 h-5 mr-2" /> LOW PAY
                      </Button>
                    </div>
                  </div>
                </div>
              ) : (
                <div className="bg-gray-50 rounded-xl p-8 text-center border border-gray-200">
                  <div className="w-16 h-16 bg-gray-200 rounded-full flex items-center justify-center mx-auto mb-4">
                    {isScanning ? (
                      <RefreshCw className="w-8 h-8 text-purple-500 animate-spin" />
                    ) : (
                      <Activity className="w-8 h-8 text-gray-400" />
                    )}
                  </div>
                  <h3 className="text-lg font-medium text-gray-700 mb-2">
                    {isScanning ? 'Scanning for Signals...' : 'Scanner Off'}
                  </h3>
                  <p className="text-gray-500 text-sm">
                    {scannerEnabled 
                      ? `Analyzing ${tradingPairs.length} ${selectedMarket.toUpperCase()} pairs for high-probability signals`
                      : 'Turn on the scanner to start finding signals'
                    }
                  </p>
                </div>
              )}
            </div>
            
            {/* Technical Indicators */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-700 text-sm mb-4">
                TECHNICAL INDICATORS — {selectedPair?.symbol || 'Select a pair'}
              </h3>
              <div className="grid grid-cols-6 gap-4">
                <div>
                  <p className="text-xs text-gray-500">Fast EMA (5)</p>
                  <p className="font-mono font-medium text-gray-900">{indicators.fastEMA.toFixed(5)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Slow EMA (13)</p>
                  <p className="font-mono font-medium text-gray-900">{indicators.slowEMA.toFixed(5)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">RSI (7)</p>
                  <p className={`font-mono font-medium ${indicators.rsi < 30 ? 'text-green-600' : indicators.rsi > 70 ? 'text-red-600' : 'text-gray-900'}`}>
                    {indicators.rsi.toFixed(2)}
                  </p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">BB Upper</p>
                  <p className="font-mono font-medium text-gray-900">{indicators.bbUpper.toFixed(5)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">BB Basis</p>
                  <p className="font-mono font-medium text-gray-900">{indicators.bbBasis.toFixed(5)}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">BB Lower</p>
                  <p className="font-mono font-medium text-gray-900">{indicators.bbLower.toFixed(5)}</p>
                </div>
              </div>
            </div>
            
            {/* Signal History */}
            <div className="bg-white rounded-xl border border-gray-200 p-4">
              <h3 className="font-semibold text-gray-700 text-sm mb-4">SIGNAL HISTORY</h3>
              <div className="space-y-2 max-h-64 overflow-y-auto">
                {signalHistory.length > 0 ? signalHistory.map((signal, idx) => (
                  <div 
                    key={signal.id || idx}
                    className={`flex items-center justify-between p-3 rounded-lg border ${
                      signal.result === 'win' ? 'bg-green-50 border-green-200' :
                      signal.result === 'lost' ? 'bg-red-50 border-red-200' :
                      'bg-gray-50 border-gray-200'
                    }`}
                  >
                    <div className="flex items-center gap-4">
                      <div className={`w-1 h-10 rounded-full ${
                        signal.result === 'win' ? 'bg-green-500' :
                        signal.result === 'lost' ? 'bg-red-500' :
                        'bg-gray-400'
                      }`}></div>
                      <div>
                        <p className="font-medium text-gray-900">{signal.pair}</p>
                        <p className="text-xs text-gray-500">@ {formatPrice(signal.entryPrice, signal.pair)}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-4">
                      <span className={`text-sm px-2 py-0.5 rounded ${signal.strength >= 50 ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'}`}>
                        {signal.strength}%
                      </span>
                      <span className="text-sm bg-gray-100 px-2 py-0.5 rounded text-gray-600">{signal.payout}%</span>
                      <span className={`font-bold text-sm ${signal.direction === 'CALL' ? 'text-green-600' : 'text-red-600'}`}>
                        {signal.direction === 'CALL' ? '↗' : '↘'} {signal.direction}
                      </span>
                      <span className="text-xs text-gray-500">{new Date(signal.timestamp).toLocaleTimeString()}</span>
                    </div>
                  </div>
                )) : (
                  <div className="text-center py-8 text-gray-500">
                    <p>No signals yet. Start the scanner to find signals.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      
      {/* Mobile Switch Button */}
      <div className="lg:hidden fixed bottom-4 right-4 flex flex-col gap-2">
        <button 
          onClick={() => setShowChartAnalyzer(true)} 
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-full shadow-lg font-medium"
        >
          <Camera className="w-4 h-4" />
          Chart Analyzer
        </button>
        <button 
          onClick={() => setShowPlanner(true)} 
          className="flex items-center gap-2 px-4 py-2 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-full shadow-lg font-medium"
        >
          <Calculator className="w-4 h-4" />
          Game Planner
        </button>
        <button onClick={onSwitchToStandard} className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-full shadow-lg font-medium">
          Go Standard
        </button>
      </div>
      
      {/* Desktop PRO Tools Buttons - Fixed position */}
      <div className="hidden lg:flex fixed bottom-6 left-6 gap-3">
        <button
          onClick={() => setShowChartAnalyzer(true)}
          className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-blue-500 to-purple-600 text-white rounded-xl shadow-lg font-semibold hover:from-blue-600 hover:to-purple-700 transition-all transform hover:scale-105"
        >
          <Camera className="w-5 h-5" />
          Chart Analyzer
        </button>
        <button
          onClick={() => setShowPlanner(true)}
          className="flex items-center gap-2 px-5 py-3 bg-gradient-to-r from-yellow-500 to-orange-500 text-white rounded-xl shadow-lg font-semibold hover:from-yellow-600 hover:to-orange-600 transition-all transform hover:scale-105"
        >
          <Calculator className="w-5 h-5" />
          Financial Game Planner
        </button>
      </div>
    </div>
      )}
    </>
  );
};

export default ProScalperBot;
